import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface SelectableListItem<T extends string> {
  value: T;
  label: ReactNode;
  /** Its name for screen readers and type-ahead, when `label` isn't plain text. */
  textValue: string;
  /** Before the label: a StatusDot. */
  leading?: ReactNode;
  /** After the label: a CountBadge, small icons. */
  trailing?: ReactNode;
  /** Hover text for the row (the shared `data-hint`). */
  hint?: string;
  /** Muted label, for rows that are there but empty ("Paste to preview"). */
  muted?: boolean;
}

export interface SelectableListGroup<T extends string> {
  label: string;
  items: readonly SelectableListItem<T>[];
}

export interface SelectableListProps<T extends string> {
  /** What the list is (its accessible name). */
  'aria-label': string;
  groups: readonly SelectableListGroup<T>[];
  value: T | null;
  onValueChange: (value: T) => void;
  className?: string;
}

/**
 * A list to pick one item from, in named groups, beside what it shows (the flow editor's
 * templates). A listbox: one Tab stop, the arrow keys move and choose, Enter or Space chooses the focused
 * row, Home and End jump, typing a
 * letter moves to the next item starting with it. Each row has a leading dot, its name and trailing
 * markers.
 */
export function SelectableList<T extends string>({
  groups,
  value,
  onValueChange,
  className,
  ...aria
}: SelectableListProps<T>) {
  const items = groups.flatMap((g) => g.items);
  const refs = useRef(new Map<T, HTMLDivElement>());
  const selected = items.findIndex((i) => i.value === value);
  const tabStop = selected >= 0 ? selected : 0;

  const choose = (index: number) => {
    const item = items[index];
    if (!item) return;
    onValueChange(item.value);
    const el = refs.current.get(item.value);
    el?.focus();
    el?.scrollIntoView({ block: 'nearest' });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>, index: number) => {
    const last = items.length - 1;
    const moves: Record<string, number> = {
      ArrowDown: Math.min(last, index + 1),
      ArrowUp: Math.max(0, index - 1),
      Home: 0,
      End: last,
    };
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      choose(index);
      return;
    }
    if (event.key in moves) {
      event.preventDefault();
      choose(moves[event.key]);
      return;
    }
    if (event.key.length === 1 && /\S/.test(event.key) && !event.metaKey && !event.ctrlKey && !event.altKey) {
      const letter = event.key.toLowerCase();
      const order = [...items.slice(index + 1), ...items.slice(0, index + 1)];
      const next = order.find((i) => i.textValue.toLowerCase().startsWith(letter));
      if (next) {
        event.preventDefault();
        choose(items.indexOf(next));
      }
    }
  };

  let index = -1;
  return (
    <div role="listbox" {...aria} className={cn('flex flex-col gap-3', className)}>
      {groups.map((group) =>
        group.items.length ? (
          <div key={group.label} role="group" aria-label={group.label} className="flex flex-col gap-px">
            <div className="px-2 pb-1 text-xs font-medium text-muted-foreground" aria-hidden>
              {group.label}
            </div>
            {group.items.map((item) => {
              index++;
              const i = index;
              const isSelected = item.value === value;
              return (
                <div
                  key={item.value}
                  ref={(el) => {
                    if (el) refs.current.set(item.value, el);
                    else refs.current.delete(item.value);
                  }}
                  role="option"
                  aria-selected={isSelected}
                  aria-label={item.textValue}
                  tabIndex={i === tabStop ? 0 : -1}
                  data-hint={item.hint}
                  onClick={() => choose(i)}
                  onKeyDown={(e) => onKeyDown(e, i)}
                  className={cn(
                    'flex h-8 cursor-pointer items-center gap-2 rounded-md px-2 text-sm outline-none select-none',
                    'hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring',
                    isSelected && 'bg-muted font-medium'
                  )}
                >
                  <span className="flex size-3 shrink-0 items-center justify-center">{item.leading}</span>
                  <span className={cn('min-w-0 flex-1 truncate', item.muted && 'text-muted-foreground')}>{item.label}</span>
                  {item.trailing ? <span className="flex shrink-0 items-center gap-1.5">{item.trailing}</span> : null}
                </div>
              );
            })}
          </div>
        ) : null
      )}
    </div>
  );
}
